/**
 * Shared vocabularies and helpers for the Elections admin tool
 * (Documentation/ELECTIONS_TOOL_PLAN.md). Consolidates the enums that the
 * legacy pages kept in-file. Values are Colorado-specific by decision —
 * centralized here so multi-state work later touches one module.
 */

export interface LabeledValue {
  value: string;
  label: string;
}

/** Cycle-level election types (election_calendars.election_type). */
export const CYCLE_ELECTION_TYPES: LabeledValue[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'special', label: 'Special' },
  { value: 'vacancy', label: 'Vacancy' },
  { value: 'recall', label: 'Recall' },
];

/**
 * Milestone types are an admin-managed vocabulary (election_milestone_types,
 * Documentation/ELECTION_MILESTONE_TYPES_PRD.md) served by
 * GET /api/election-milestone-types — see admin/hooks/useMilestoneTypes.
 *
 * These values are the exception: application code looks them up by name, so
 * the database marks them is_system (they cannot be deleted, retired, or
 * re-valued). Adding a new code dependency on a type value means marking it
 * is_system in a migration and listing it here.
 */
export const SYSTEM_MILESTONE_TYPES = {
  PRIMARY: 'primary',
  GENERAL: 'general',
  REGISTRATION_DEADLINE: 'registration_deadline',
  BALLOTS_MAILED: 'ballots_mailed',
} as const;

/** What kind of date a milestone type is (election_milestone_types.category). */
export const MILESTONE_CATEGORIES: LabeledValue[] = [
  { value: 'election_day', label: 'Election day' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'voting_window', label: 'Voting window' },
  { value: 'informational', label: 'Informational' },
  { value: 'administrative', label: 'Administrative' },
];

export const milestoneCategoryLabel = (category: string | null | undefined): string =>
  MILESTONE_CATEGORIES.find((entry) => entry.value === category)?.label ?? (category || '—');

/** "7 days, 1 day and the day of" — a reminder schedule in words. */
export function describeLeadDays(days: number[] | null | undefined): string {
  const parts = [...(days ?? [])]
    .sort((a, b) => b - a)
    .map((day) => (day === 0 ? 'the day of' : `${day} day${day === 1 ? '' : 's'} before`));
  if (parts.length <= 1) return parts[0] ?? 'never';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Compact schedule for table cells: "14 · 7 · 1" / "1 · day of". */
export function shortLeadDays(days: number[] | null | undefined): string {
  return [...(days ?? [])]
    .sort((a, b) => b - a)
    .map((day) => (day === 0 ? 'day of' : String(day)))
    .join(' · ');
}

/**
 * The YYYY-MM-DD dates a milestone on `milestoneDate` would remind on, given
 * its type's schedule — only those on or after `todayISO` (reminders are never
 * back-filled), earliest first.
 */
export function upcomingReminderDates(milestoneDate: string, leadDays: number[], todayISO: string): string[] {
  const [year, month, day] = milestoneDate.slice(0, 10).split('-').map(Number);
  return [...leadDays]
    .sort((a, b) => b - a)
    .map((lead) => toLocalDateString(new Date(year, month - 1, day - lead)))
    .filter((date) => date >= todayISO);
}

export const OFFICE_LEVELS: LabeledValue[] = [
  { value: 'federal', label: 'Federal' },
  { value: 'state_executive', label: 'State Executive' },
  { value: 'state_legislative', label: 'State Legislative' },
  { value: 'state_board', label: 'State Board' },
  { value: 'judicial', label: 'Judicial' },
  { value: 'county', label: 'County' },
  { value: 'municipal', label: 'Municipal' },
  { value: 'special_district', label: 'Special District' },
];

export const officeLevelLabel = (level: string | null): string =>
  OFFICE_LEVELS.find((entry) => entry.value === level)?.label ?? (level || '—');

export const OFFICE_SCOPES: LabeledValue[] = [
  { value: 'statewide', label: 'Statewide' },
  { value: 'by_district', label: 'By district' },
];

/** Contest-level election types (contests.election_type — distinct from cycle types). */
export const CONTEST_ELECTION_TYPES: LabeledValue[] = [
  { value: 'general', label: 'General' },
  { value: 'primary', label: 'Primary' },
  { value: 'special', label: 'Special' },
  { value: 'recall', label: 'Recall' },
];

/** Party applies to primary contests only (DB CHECK enforces it). */
export const CONTEST_PARTY_OPTIONS = ['Democratic', 'Republican', 'Libertarian', 'Unaffiliated', 'Other'];

export const CONTEST_STATUSES: LabeledValue[] = [
  { value: 'expected', label: 'Expected' },
  { value: 'official', label: 'Official' },
];

/** The two sides of a ballot measure (arguments, endorsements, finance rollups). */
export const INITIATIVE_SIDES = [
  { key: 'support', label: 'Support', accent: 'text-success-700 dark:text-success-400' },
  { key: 'oppose', label: 'Oppose', accent: 'text-error-700 dark:text-error-400' },
] as const;

export type InitiativeSide = (typeof INITIATIVE_SIDES)[number]['key'];

/** Where a ballot-measure argument comes from (ballot_initiative_arguments.source_type). */
export const ARGUMENT_SOURCE_TYPES: LabeledValue[] = [
  { value: 'blue_book', label: 'Blue Book' },
  { value: 'committee', label: 'Committee' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'other', label: 'Other' },
];

const toLocalDateString = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Colorado general election date for a year: the Tuesday after the first
 * Monday in November.
 */
export function coGeneralElectionDate(year: number): Date {
  const nov1 = new Date(year, 10, 1);
  const offsetToFirstMonday = (1 - nov1.getDay() + 7) % 7;
  return new Date(year, 10, 1 + offsetToFirstMonday + 1);
}

/**
 * Default cycle window for an election year: the day after the prior general
 * election through this year's general election (Colorado's statutory
 * campaign-finance cycle). Admins can override; the calendar row is
 * authoritative.
 */
export function defaultCycleWindow(year: number): { start: string; end: string } {
  const dayAfterPriorGeneral = coGeneralElectionDate(year - 2);
  dayAfterPriorGeneral.setDate(dayAfterPriorGeneral.getDate() + 1);
  return {
    start: toLocalDateString(dayAfterPriorGeneral),
    end: toLocalDateString(coGeneralElectionDate(year)),
  };
}
